import reduce from 'lodash/reduce';
import get from 'lodash/get';
import find from 'lodash/find';
import map from 'lodash/map';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import toLower from 'lodash/toLower';
import flatten from 'lodash/flatten';

// helpers
export const convertToPricision = (number = 0) => {
    const parsedNumber = Number(number);
    /* eslint-disable no-restricted-globals */
    return !isNaN(parsedNumber) ? parsedNumber.toFixed(2) : number;
};

// common function & selectors
// can be exported based on need

// get valid item
export const getValidItem = (item = {}) =>
    isEmpty(item.product) ? get(item, 'childItems[0]', {}) : item;

// get total attribute
export const getTotal = (item = {}, name = '') =>
    (find(item.totals, { name }) || { amount: '' }).amount;

// get price attribute
export const getPrice = (item = {}, type = '') =>
    (find(item.prices, { type }) || { amount: '' }).amount;

// get charge attribute
export const getCharge = (item = {}, name = '') =>
    (find(item.charges, { name }) || { amount: '' }).amount;

// get discount attribute
export const getDiscount = (item = {}, code = '') =>
    (find(item.discounts, { code }) || { amount: '' }).amount;

export const getPriceTypeByAmount = (item, amount) =>
    (find((item || {}).prices, { amount }) || { type: '' }).type;

// get sku attribute
export const getSkuAttribute = (item = {}, name = '') =>
    toLower((find(get(item, 'product.sku.attributes'), { name }) || { value: '' }).value);

// get PriceInfo attribute
export const getInvoicePrice = (priceInfo = {}, code = '') =>
    (find(priceInfo, { code }) || { value: '' }).value;

// get order sum from
export const getOrderCancellationPrice = (products) =>
    reduce(
        products,
        (orderSum, product) =>
            orderSum + (Number(get(product, 'productInfo.productCancellationPrice', 0)) || 0),
        0
    );

const paymentTypeExceptionConfig = {
    unnamed: 'giftcard',
    creditCards: 'creditcard',
};
// get Payment type
export const getPaymentTypes = (paymentInfo = {}) =>
    map(paymentInfo.items, (...args) => {
        /* istanbul ignore next */
        const key = args[1];
        return paymentTypeExceptionConfig[key] ? paymentTypeExceptionConfig[key] : toLower(key);
    });

const deliveryMethodExceptionConfig = {
    'Ship To Store': 'ship to store',
    'HOME DELIVERY': 'ship to home',
    'Factory Ship': 'ship to home',
    'SAME DAY PICKUP IN STORE': 'same day pickup',
};

export const getProductDeliveryMethod = (item = {}) => {
    const deliveryMethod = get(item, 'shipRouteDesc', '');
    return deliveryMethodExceptionConfig[deliveryMethod] || toLower(deliveryMethod);
};

const ccTypeExceptionConfig = {
    JCP: 'jcp',
    JCP_CARD: 'jcp',
    VISA: 'visa',
    PAYPAL: 'paypal', // this should be covered
    AMERICAN_EXPRESS: 'american express',
    'AMERICAN EXPRESS': 'american express',
    AMEX: 'american express',
    MASTERCARD: 'mastercard',
    MASTER_CARD: 'mastercard',
    MASTER: 'mastercard',
    DISCOVER: 'discover',
    JCPMASTER: 'jcpenney mastercard',
    JCPENNY_MASTER_CARD: 'jcpenney mastercard',
};
export const getCCTypesString = (cards) =>
    reduce(
        cards,
        (joinedType, card) => {
            // ccType = jcp | visa | mastercard | paypal | discover | american express
            const newCardType = ccTypeExceptionConfig[card.type] || toLower(card.type);
            return (joinedType && [joinedType, newCardType].join('|')) || newCardType;
        },
        ''
    );
const cadenceTypeConfig = {
    // BE value : //Analytics value
    PRICEQUOTE: 'pricequote',
    FORCEDPRICE: 'forcedprice',
    ORIGINAL: 'regular',
    SALE: 'sale',
    CLEARANCE: 'clearance',
    ACTIVE: 'active',
    NFORPRICE: 'buy more and save',
};
export const getProductPricingCadence = (item = {}) => {
    // this logic is suggested by BE
    const itemPrice = getTotal(item, 'ItemPrice');

    const priceType = getPriceTypeByAmount(item, itemPrice);

    return cadenceTypeConfig[priceType] || priceType;
};

// shipping method
const shippingMethodConfig = {
    STANDARD: 'standard delivery',
    GROUND3DAY: 'premium delivery 3 business days',
    EXPRESS_ECONOMY: 'express delivery 2 business days',
    EXPRESS: 'expedited delivery 1 business day',
    TRUCK: 'truck delivery',
};

export const getShippingMethod = (invoiceInfo = []) => {
    if (isEmpty(invoiceInfo)) return '';

    const code = get(invoiceInfo, '[0].shippingMethod', '');
    const shippingMethod = shippingMethodConfig[code] || toLower(code);

    return shippingMethod;
};

// is Item cancellable
export const isItemCancellable = (item = {}) => (item.actions || []).includes('CANCEL');

// convert item from order details item --> Slider ItemCard format
export const formatToGetItems = (item = {}) => {
    if (isEmpty(item)) return {};
    const validItem = getValidItem(item);
    const product = validItem.product || {};
    return {
        productid: product.id,
        name: product.name,
        imageURL: product.imageURL,
        lineId: get(item, 'itemNumber', ''),
        // lineId: get(product, 'sku.id', ''),
        qty: get(product, 'sku.quantity', 0),
        totalAmount: getTotal(item, 'Total Price'),
        perUnitAmount: undefined, // As per new contract, not required
        childItems: get(item, 'childrenId', []), // To be verified from BE
        displayItem: get(item, 'displayItem', false),
    };
};

// Single invoice items --> split to format [ cancellable, noncancellable]
// formator needs to pass as params
export const formCancelItemsMap = (invoiceItems, formator) =>
    reduce(
        invoiceItems,
        ([cancellable, noncancellable], item) => {
            const validItem = getValidItem(item);
            if (isItemCancellable(validItem)) {
                /* eslint-disable no-param-reassign */
                cancellable = cancellable.concat(formator(validItem));
            } else {
                /* eslint-disable no-param-reassign */
                noncancellable = noncancellable.concat(formator(validItem));
            }

            return [cancellable, noncancellable];
        },
        [[], []]
    );

export const aggregateGetItemResponse = (orderDetailsResponse = {}) => {
    // console.log('order details input', orderDetailsResponse);

    if (isEmpty(orderDetailsResponse.invoiceInfo)) return null;

    const { id } = orderDetailsResponse;

    // TODO: round up charity

    // this is done as per order details
    const itemCancelRestriction = !!orderDetailsResponse.restricItemLevelCancel;

    const otherCancellableItems = get(orderDetailsResponse, 'otherCancellableItems', []);

    const reasons = get(orderDetailsResponse, 'reasons', []);
    const [cancellable, noncancellable] = reduce(
        orderDetailsResponse.invoiceInfo,
        ([cancellableComposer, noncancellableComposer], invoice) => {
            const [newCancellableMap, newNoncancellableMap] = formCancelItemsMap(
                invoice.items,
                formatToGetItems
            ); // items, formator function
            return [
                cancellableComposer // previous invoice processed data
                    .concat(newCancellableMap), // new invoice processed data
                noncancellableComposer // previous invoice processed data
                    .concat(newNoncancellableMap), // new invoice processed data
            ];
        },
        [
            [], // cancellable initial value
            [], // noncancellable initial value
        ]
    );

    return {
        id,
        cancellable,
        noncancellable,
        itemCancelRestriction,
        reasons,
        otherCancellableItems,
    };
};

// analytics format
export const formatToProductInfo = (item = {}) => {
    if (isEmpty(item)) return {};
    // console.log('valid Item', item);

    const productPPID = get(item, 'product.ppId', '');
    const productSKU = get(item, 'product.sku.id', '');
    const lotIdValue = get(item, 'product.lotNumber', '');
    const productLotId = [lotIdValue];
    const productName = toLower(get(item, 'product.name', ''));
    const productListPrice = convertToPricision(getPrice(item, 'ORIGINAL'));
    const productCurrentSellingPrice = convertToPricision(getTotal(item, 'ItemPrice'));
    const productQuantity = String(get(item, 'product.sku.quantity', 0));
    const productPricingCadence = getProductPricingCadence(item);
    const productDeliveryMethod = getProductDeliveryMethod(item);
    const productMarketingLabel = get(item, 'product.marketingLabel', 'no marketing label'); // no mapping from BE
    const productSurchargePrice = convertToPricision(getCharge(item, 'OVERSIZED_SURCHARGE'));
    const productCouponAmount = convertToPricision(getDiscount(item, 'COUPON'));
    const productRewardsAmount = convertToPricision(getDiscount(item, 'REWARDSCERT'));
    const qtyDiscountValue =
        getDiscount(item, 'NFORDISCOUNT') || getDiscount(item, 'MISCELLANEOUS');
    const productQuantityDiscount = convertToPricision(qtyDiscountValue);
    const productAssociateDiscount = convertToPricision(getDiscount(item, 'ASSOCIATEDISCOUNT'));
    const productFee = convertToPricision(getCharge(item, 'RECYCLE_CHARGE'));
    const productBundleFlag = get(item, 'product.type.type', '') === 'BUNDLE' ? 'yes' : 'no';
    const productColor = getSkuAttribute(item, 'color');
    const productSize = getSkuAttribute(item, 'combined size') || getSkuAttribute(item, 'size');
    const productSizeType = getSkuAttribute(item, 'size range');
    const productCancellationEligibility = isItemCancellable(item)
        ? 'cancellable'
        : 'not cancellable';
    const productCancellationPrice = convertToPricision(getTotal(item, 'Total Price'));
    const lineId = get(item, 'itemNumber', ''); // this will be used only for filtering based on selection in slider form

    return {
        productInfo: {
            productPPID,
            productLotId,
            productSKU,
            productName,
            productListPrice,
            productCurrentSellingPrice,
            productQuantity,
            productPricingCadence,
            productDeliveryMethod,
            productMarketingLabel,
            productSurchargePrice,
            productCouponAmount,
            productRewardsAmount,
            productQuantityDiscount,
            productAssociateDiscount,
            productFee,
            productBundleFlag,
            productColor,
            productSize,
            productSizeType,
            productCancellationEligibility,
            productCancellationPrice,
            lineId,
        },
    };
};

export const aggregateAnalyticsData = (orderDetailsResponse = {}) => {
    if (isEmpty(orderDetailsResponse)) return {};
    // console.log(orderDetailsResponse);
    const { invoiceInfo, priceInfo, paymentInfo } = orderDetailsResponse;
    // form product key in analytics using aggregator
    const product = reduce(
        invoiceInfo,
        (cancellableItemList, invoice) => {
            const newcancellableItemList = flatten(
                formCancelItemsMap(invoice.items, formatToProductInfo)
            ); // [cancellableArray, noncancellableArray]

            // const cancellableItemLis
            // console.log(cancellableItemList, invoice);
            return cancellableItemList.concat(newcancellableItemList);
        },
        [] // default value of product
    );

    // form transaction object
    // --> total
    const shippingCost = convertToPricision(getInvoicePrice(priceInfo, 'SHIPPING AND HANDLING'));

    const orderSubtotalValue = getInvoicePrice(priceInfo, 'SUB TOTAL AFTER ADJUSTMENT');
    const merchandiseSubTotalValue = getInvoicePrice(priceInfo, 'SUB TOTAL');
    const merchandiseSubTotal = convertToPricision(merchandiseSubTotalValue);
    const orderSubTotal = convertToPricision(
        orderSubtotalValue || merchandiseSubTotal - getInvoicePrice(priceInfo, 'SAVINGS')
    );
    const orderTotal = convertToPricision(getInvoicePrice(priceInfo, 'ORDER AMOUNT'));
    const totalTax = convertToPricision(getInvoicePrice(priceInfo, 'TAX'));
    const overSizedItemSurcharge = convertToPricision(
        getInvoicePrice(priceInfo, 'OVERSIZED SURCHARGE')
    );
    const jcpGiftCardAmount = convertToPricision(getInvoicePrice(priceInfo, 'GIFTWRAP'));

    const total = {
        shippingCost,
        orderSubTotal,
        merchandiseSubTotal,
        orderTotal,
        totalTax,
        overSizedItemSurcharge,
        jcpGiftCardAmount,
    };

    // --> attributes

    const paymentType = getPaymentTypes(paymentInfo);
    const ccType = getCCTypesString(get(paymentInfo, 'items.creditCards'));
    const shippingMethod = getShippingMethod(invoiceInfo);

    const attributes = {
        paymentType,
        ccType,
        transactionID: orderDetailsResponse.id,
        shippingMethod,
        // cancellationReason: '', // should be done in saga or middleware
        // cancellationOrderType: '', // should be done in saga or middleware
        // cancellationStatus: '', //  should be done in saga or middleware
        // orderCancellationPrice: '', //  should be done in saga or middleware
    };
    const transaction = {
        total,
        attributes,
    };

    const couponRewardsInfo = [];
    let couponRewardItem = {};
    const couponAmount = getInvoicePrice(priceInfo, 'COUPON');
    const rewardAmount = getInvoicePrice(priceInfo, 'REWARDSCERT');

    if (rewardAmount) {
        couponRewardItem = {
            rewardValid: 'yes',
            rewardAmount: convertToPricision(rewardAmount),
        };
        couponRewardsInfo.push(couponRewardItem);
    }

    if (couponAmount) {
        couponRewardItem = {
            couponEligible: 'yes',
            couponAmount: convertToPricision(couponAmount),
        };
        couponRewardsInfo.push(couponRewardItem);
    }

    const analyticsObj = {
        product,
        transaction,
        couponRewardsInfo,
    };
    return analyticsObj;
};

// process analytics data based on submitted form data
// as this is success scnerio
// we can filter the selected-cancellable item
// from stored redux analytics data
// and do some calculation as
export const processAnalyticsData = (analyticsData = {}, formData = {}) => {
    if (isEmpty(analyticsData) || isEmpty(formData)) return {};

    const lines = get(formData, 'data.lines', []);
    const { product = [], transaction = {} } = analyticsData;
    const { attributes = {} } = transaction;
    // selected product list
    const selectedProductList = filter(product, (item) =>
        lines.includes(get(item, 'productInfo.lineId', ''))
    );

    // new transaction attributes
    const cancellationReason = toLower(get(formData, 'data.reasonCode', ''));
    const cancellationOrderType =
        selectedProductList.length === product.length ? 'full' : 'partial';
    const orderCancellationPrice = convertToPricision(
        getOrderCancellationPrice(selectedProductList)
    );
    const cancellationStatus = 'success';

    const newAttributesObj = {
        ...attributes,
        cancellationReason,
        cancellationOrderType,
        cancellationStatus,
        orderCancellationPrice,
    };

    const newTransactionObj = {
        ...transaction,
        attributes: newAttributesObj,
    };

    // console.log('analyticsData', analyticsData, 'formData', formData, 'selectedProductList', selectedProductList);
    return {
        product: selectedProductList,
        transaction: newTransactionObj,
    };
};

export const isGetItemsDataValid = (data) => {
    if (isEmpty(data) || get(data, 'cancellable', []).length === 0) return false;

    return true;
};
